import React, { useContext, useEffect, useState } from "react";
import { Block } from "baseui/block";
import { BiMessageError } from "react-icons/bi";
import { Textarea } from "baseui/textarea";
import { Button } from "baseui/button";
import AuthContext from "../shared/AuthContext";
import QuestionTimestamp from "./QuestionTimestamp";

const ExceptionItem = ({ setStatusMessage, questionId, userId }) => {
    const { apiClient } = useContext(AuthContext);
    const [exceptions, setExceptions] = useState([]);
    const [exceptionMessage, setExceptionMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    // Fetch exceptions on component mount
    useEffect(() => {
        const getExceptions = async () => {
            try {
                setIsLoading(true);
                const response = await apiClient.get(`/api/get-exceptions?questionId=${questionId}&userId=${userId}`);

                if (response.status === 200 && response.data) {
                    setExceptions(response.data || []);
                }
            } catch (err) {
                console.error("Error fetching exceptions:", err.message);
            } finally {
                setIsLoading(false);
            }
        };

        getExceptions();
    }, [questionId, userId]);

    // Handle adding a new exception
    const handleException = async () => {
        if (!exceptionMessage.trim()) return;

        const payload = { questionId, userId, exceptionMessage };

        try {
            setIsLoading(true);
            const response = await apiClient.post("/api/add-exception", payload);

            // Log API response and check for the structure
            console.log("API Response:", response);

            if (response.status === 200) {
                setStatusMessage(response.statusText);

                // Log the new exception data
                console.log("New Exception Data:", response.data);

                // Update exceptions list with the new exception at the top
                setExceptions(response.data);

                // Clear the input field after successful submission
                setExceptionMessage("");
            }
        } catch (err) {
            console.error("Error adding exception:", err.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Block padding="12px">
            {/* Exception Input Section */}
            <label style={{ display: "flex", alignItems: "center", marginBottom: "8px", fontSize: "smaller" }}>
                {/*<BiMessageError size={15} style={{ marginRight: "6px" }} />*/}
                Raise an exception
            </label>

            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Textarea
                    value={exceptionMessage}
                    onChange={(e) => setExceptionMessage(e.target.value)}
                    resize="both"
                    rows={1}
                    size="mini"
                    placeholder="Exception message"
                    style={{ flex: 1 }}
                />
                <Button size="mini" onClick={handleException} isLoading={isLoading}>
                    Raise
                </Button>
            </div>

            {/* Exception Table */}
            {exceptions.length > 0 ? (
                <Block style={{ marginTop: "10px", maxHeight: "180px", overflowY: "auto", border: "1px solid #ddd", borderRadius: "6px" }}>
                    <table style={{ width: "100%", fontSize: "12px", borderCollapse: "collapse" }}>
                        <thead style={{ background: "#f1f1f1" }}>
                        <tr>
                            <th style={{ ...styles.th, textAlign: "center" }}>Message</th>
                            <th style={{ ...styles.th, textAlign: "center" }}>Status</th>
                            <th style={{ ...styles.th, textAlign: "center" }}>Added On</th>
                        </tr>
                        </thead>
                        <tbody>
                        {exceptions.map((ex) => (
                            <tr key={ex.id} style={{ borderBottom: "1px solid #ddd" }}>
                                <td style={{
                                    ...styles.td,
                                    wordWrap: "break-word",
                                    whiteSpace: "normal",
                                    maxWidth: "150px",  // Optional: Set max width if you want to limit cell size
                                    overflow: "hidden", // Prevent overflow beyond the cell's boundary
                                    textOverflow: "ellipsis" // Optional: Add ellipsis if text is too long and exceeds the max width
                                }}>
                                    {ex.exceptionMessage}
                                </td>

                                <td style={styles.td}>
                                    {ex.status ? (
                                        <span style={styles.activeStatus}>🟢 New</span>
                                    ) : (
                                        <span style={styles.inactiveStatus}>🔴 Resolved</span>
                                    )}
                                </td>
                                <td style={styles.td}>
                                    <QuestionTimestamp addedOn={ex.addedOn} />
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </Block>
            ) : (
                !isLoading && <p style={{fontSize:'smaller'}}></p> // Display message if there are no exceptions
            )}
        </Block>
    );
};

// Table styling
const styles = {
    th: {
        padding: "6px",
        textAlign: "left",
        fontWeight: "bold",
        borderBottom: "2px solid #ddd",
    },
    td: {
        padding: "6px",
        borderBottom: "1px solid #ddd",
        wordWrap: "break-word",
        whiteSpace: "normal",
        maxWidth: "150px", // Optional: Ensure text wraps or truncates
        overflow: "hidden",
        textOverflow: "ellipsis"
    },
    activeStatus: {
        color: "green",
        fontWeight: "bold",
    },
    inactiveStatus: {
        color: "red",
        fontWeight: "bold",
    },
};

export default ExceptionItem;
